import {
  U21_MARKDOWN_COMPONENTS,
  U21Button,
  U21ButtonProps,
  U21Markdown,
  U21MarkdownProps,
} from 'app/shared/u21-ui/components';
import { matchPath } from 'react-router';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { ComponentProps, useMemo } from 'react';
import {
  useToggleActionEventSidebar,
  useToggleAlertSidebar,
  useToggleCaseSidebar,
  useToggleEntitySidebar,
  useToggleFinCENSARSidebar,
  useToggleInstrumentSidebar,
  useToggleTransactionSidebar,
} from 'app/modules/sidebar/hooks';
import { useTypewriterEffect } from 'app/modules/ai/hooks/useTypewriterEffect';
import { TypewriterOptions } from 'app/modules/ai/types';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';

interface Props extends U21MarkdownProps {
  animate?: boolean;
  typewriterOptions?: TypewriterOptions;
}

export const AIMarkdown = (props: Props) => {
  const { animate = false, children, typewriterOptions, ...rest } = props;

  const { displayedText, isComplete } = useTypewriterEffect(
    children,
    animate,
    typewriterOptions,
  );
  return (
    <motion.div
      initial={animate ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <U21Markdown
        {...rest}
        components={{ ...COMPONENT_OVERRIDES, ...props?.components }}
      >
        {displayedText}
      </U21Markdown>
      {animate && !isComplete && <BlinkingCursor />}
    </motion.div>
  );
};

const BLINK = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const BlinkingCursor = styled.span`
  &::after {
    content: '|';
    animation: ${BLINK} 1s step-end infinite;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const A = (props: ComponentProps<(typeof U21_MARKDOWN_COMPONENTS)['a']>) => {
  const { href = '', children } = props;

  const toggleAlertSidebar = useToggleAlertSidebar();
  const toggleCaseSidebar = useToggleCaseSidebar();
  const toggleFinCENSARSidebar = useToggleFinCENSARSidebar();
  const toggleEntitySidebar = useToggleEntitySidebar();
  const toggleInstrumentSidebar = useToggleInstrumentSidebar();
  const toggleTransactionsSidebar = useToggleTransactionSidebar();
  const toggleActionEventsSidebar = useToggleActionEventSidebar();

  const openSidePanel = useMemo<U21ButtonProps['onClick']>(() => {
    if (!href) {
      return undefined;
    }

    // if protocol is defined, link externally
    if (href.startsWith('http://') || href.startsWith('https://')) {
      return undefined;
    }

    const alertMatch = matchPath<{ id: string }>(href, {
      path: ROUTES_MAP.alertsId.path,
    });
    if (alertMatch && Number(alertMatch.params.id)) {
      return (e) => toggleAlertSidebar(Number(alertMatch.params.id), e);
    }

    const caseMatch = matchPath<{ id: string }>(href, {
      path: ROUTES_MAP.casesId.path,
    });
    if (caseMatch && Number(caseMatch.params.id)) {
      return (e) => toggleCaseSidebar(Number(caseMatch.params.id), e);
    }

    const fincenSARMatch = matchPath<{ id: string }>(href, {
      path: ROUTES_MAP.filingsSarId.path,
    });
    if (fincenSARMatch && Number(fincenSARMatch.params.id)) {
      return (e) => toggleFinCENSARSidebar(Number(fincenSARMatch.params.id), e);
    }

    const entityMatch = matchPath<{ id: string }>(href, {
      path: ROUTES_MAP.entitiesId.path,
    });
    if (entityMatch && Number(entityMatch.params.id)) {
      return (e) => toggleEntitySidebar(Number(entityMatch.params.id), e);
    }

    const instrumentMatch = matchPath<{ id: string }>(href, {
      path: ROUTES_MAP.instrumentsId.path,
    });
    if (instrumentMatch && Number(instrumentMatch.params.id)) {
      return (e) =>
        toggleInstrumentSidebar(Number(instrumentMatch.params.id), e);
    }

    const transactionMatch = matchPath<{ id: string }>(href, {
      path: ROUTES_MAP.dataExplorerTransactionsId.path,
    });
    if (transactionMatch && Number(transactionMatch.params.id)) {
      return (e) =>
        toggleTransactionsSidebar(Number(transactionMatch.params.id), e);
    }

    const actionEventMatch = matchPath<{ id: string }>(href, {
      path: ROUTES_MAP.dataExplorerActionEventsId.path,
    });
    if (actionEventMatch && Number(actionEventMatch.params.id)) {
      return (e) =>
        toggleActionEventsSidebar(Number(actionEventMatch.params.id), e);
    }

    return undefined;
  }, [
    href,
    toggleActionEventsSidebar,
    toggleAlertSidebar,
    toggleCaseSidebar,
    toggleEntitySidebar,
    toggleFinCENSARSidebar,
    toggleInstrumentSidebar,
    toggleTransactionsSidebar,
  ]);

  if (openSidePanel) {
    return (
      <LinkButton color="primary" onClick={openSidePanel} variant="text">
        <LinkLabel>{children}</LinkLabel>
      </LinkButton>
    );
  }

  const Component = U21_MARKDOWN_COMPONENTS.a;
  return <Component {...props} />;
};

// override so there are no collapsed for printing
const COMPONENT_OVERRIDES: U21MarkdownProps['components'] = {
  a: A,
};

const LinkButton = styled(U21Button)`
  max-width: 100%;
`;

// text-overflow: ellipsis doesn't work on the inline-flex button directly,
// and using inline-block on the button breaks vertical alignment with surrounding text.
// Wrapping children in a block-level span allows ellipsis to work within the flex container.
const LinkLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;
